<template>
  <div class="notification-message" :class="messageClass">
    <div 
      ref="messageContent"
      class="notification-message__content"
      :class="{ 'is-expanded': expanded, 'is-expandable': expandable }"
    >
      <span>{{ message.text }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch, nextTick } from 'vue';
import type { NotificationMessage } from '@/types/notifications';

export interface NotificationMessageProps {
  message: NotificationMessage;
  expandable?: boolean;
  expanded?: boolean;
  maxLines?: number;
}

const props = withDefaults(defineProps<NotificationMessageProps>(), {
  expandable: false,
  expanded: false,
  maxLines: 1, // Default to single line for compact layout
});

const messageContent = ref<HTMLElement>();
const hasOverflow = ref(false);

const messageClass = computed(() => ({
  'notification-message--expandable': props.expandable,
  'notification-message--expanded': props.expanded,
  'notification-message--overflow': hasOverflow.value,
}));

const checkOverflow = () => {
  if (!messageContent.value || props.expanded) {
    hasOverflow.value = false;
    return;
  }
  
  const element = messageContent.value;
  // For single-line compact mode, check if text width exceeds container
  if (props.maxLines === 1) {
    // Use scrollHeight vs clientHeight for better detection with line-clamp
    hasOverflow.value = element.scrollHeight > element.clientHeight;
  } else {
    const lineHeight = parseInt(getComputedStyle(element).lineHeight);
    const maxHeight = lineHeight * props.maxLines;
    hasOverflow.value = element.scrollHeight > maxHeight;
  }
};

onMounted(() => {
  // Use nextTick to ensure DOM is fully rendered
  nextTick(() => {
    checkOverflow();
  });
});

watch(() => props.message, () => {
  nextTick(() => {
    checkOverflow();
  });
}, { deep: true });

watch(() => props.expanded, () => {
  nextTick(() => {
    checkOverflow();
  });
});
</script>

<style scoped lang="scss">
.notification-message {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center; // Changed from flex-start for compact layout
  gap: 4px; // Smaller gap for compact layout
  
  &__content {
    flex: 1;
    line-height: 1.3; // Slightly tighter line height
    color: var(--vscode-foreground);
    word-wrap: break-word;
    overflow-wrap: break-word;
    font-size: 13px; // Match VS Code notification font size
    
    &.is-expandable:not(.is-expanded) {
      display: -webkit-box;
      -webkit-line-clamp: 1; // Single line in compact mode
      line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      // Remove white-space: nowrap to allow line-clamp to work
    }
    
    &.is-expanded {
      -webkit-line-clamp: unset;
      line-clamp: unset;
      overflow: visible;
      white-space: normal;
      line-height: 1.4; // Normal line height when expanded
    }
  }
  
  &__expand-button {
    flex-shrink: 0;
    background: none;
    border: none;
    color: var(--vscode-textLink-foreground);
    cursor: pointer;
    padding: 1px 2px;
    margin: 0;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    
    &:hover {
      background-color: var(--vscode-toolbar-hoverBackground);
    }
    
    &:focus {
      outline: 1px solid var(--vscode-focusBorder);
      outline-offset: 1px;
    }
  }
  
  &--expandable {
    .notification-message__content {
      padding-right: 2px; // Minimal padding for compact layout
    }
  }
}
</style>
